/**
 * AI:
 * Status: 'wave-5.1-implementation'
 * Type: Util
 * SubType: Stateless rate-plan computation
 * Reason: G10 — Hebt die Raten-Berechnung (Cent-genaue Verteilung +
 *         Fälligkeits-Berechnung) aus dem Ratenvereinbarungs-Dialog in einen
 *         zustandslosen, wiederverwendbaren Util. Die Mathematik ist UNVERÄNDERT
 *         aus dem Dialog übernommen (letzte Rate trägt den Cent-Rest;
 *         plausibilityOk = sumCents === totalCents). Wird später auch von der
 *         Entscheidungstabelle wiederverwendet.
 *
 * @ticket TM-1000001782
 */

/**
 * Erlaubte Intervalle für die Fälligkeitsberechnung.
 */
export type BdRateInterval = 'MONTHLY' | 'BIWEEKLY' | 'WEEKLY';

/**
 * Eine einzelne Rate im Vorschauplan.
 */
export interface BdRateAgreementInstallment {
    index: number;
    amount: number;
    dueDate: Date;
}

/**
 * Ergebnis der Raten-Berechnung. Enthält die Einzelraten sowie die abgeleiteten
 * Kennzahlen (letzte Rate, Summe, Plausibilität).
 */
export interface BdRatePlan {
    /** Die berechneten Einzelraten (Index 1..count, Betrag, Fälligkeit). */
    installments: BdRateAgreementInstallment[];

    /** Auto-Raten-Betrag (Standardbetrag aller Raten außer ggf. der letzten). */
    rateAmount: number;

    /** Betrag der letzten Rate (trägt den Cent-Rest). */
    lastRateAmount: number;

    /** true, wenn die letzte Rate vom Auto-Raten-Betrag abweicht (Cent-Rest). */
    lastRateDiffers: boolean;

    /** Summe aller Raten (cent-genau). */
    installmentsSum: number;

    /** true, wenn die Raten-Summe cent-genau dem Gesamtbetrag entspricht. */
    plausibilityOk: boolean;
}

/**
 * Addiert {@code count} Intervalle auf das Basis-Datum. UNVERÄNDERT aus dem
 * Ratenvereinbarungs-Dialog übernommen (MONTHLY = Monats-Schritt, BIWEEKLY =
 * 14 Tage, WEEKLY = 7 Tage).
 */
export function addRatePlanInterval(base: Date, count: number, interval: BdRateInterval): Date {
    const result = new Date(base.getTime());
    if (interval === 'MONTHLY') {
        result.setMonth(result.getMonth() + count);
    } else if (interval === 'BIWEEKLY') {
        result.setDate(result.getDate() + count * 14);
    } else {
        result.setDate(result.getDate() + count * 7);
    }
    return result;
}

/**
 * Berechnet einen Raten-Plan aus Gesamtbetrag, Raten-Anzahl, Start-Datum und
 * Intervall. Reine Funktion ohne Seiteneffekte.
 *
 * C18: Cent-genaue Verteilung. Alle Raten bis auf die letzte tragen den
 * kaufmännisch gerundeten Standardbetrag; die letzte Rate trägt den
 * verbleibenden Cent-Rest, damit die Summe exakt dem Gesamtbetrag entspricht.
 *
 * Bei ungültigen Eingaben (totalAmount <= 0, count <= 0, kein startDate) wird
 * ein leerer, plausibler Plan zurückgegeben (plausibilityOk = true).
 *
 * @param totalAmount  Gesamtbetrag der Forderung.
 * @param count        Anzahl der Raten.
 * @param startDate    Fälligkeit der ersten Rate.
 * @param interval     Raten-Intervall.
 */
export function computeRatePlan(
    totalAmount: number,
    count: number,
    startDate: Date | null,
    interval: BdRateInterval
): BdRatePlan {
    if (totalAmount <= 0 || count <= 0 || !startDate) {
        return {
            installments: [],
            rateAmount: 0,
            lastRateAmount: 0,
            lastRateDiffers: false,
            installmentsSum: 0,
            plausibilityOk: true
        };
    }

    // C18: Cent-genaue Verteilung — die letzte Rate trägt den Cent-Rest.
    const totalCents = Math.round(totalAmount * 100);
    const baseCents = Math.floor(totalCents / count);
    const lastCents = totalCents - baseCents * (count - 1);
    const rateAmount = baseCents / 100;
    // A3: Letzte Rate transparent machen — sie kann vom Auto-Betrag abweichen.
    const lastRateAmount = lastCents / 100;
    const lastRateDiffers = lastCents !== baseCents;

    const installments: BdRateAgreementInstallment[] = [];
    for (let i = 0; i < count; i++) {
        const isLast = i === count - 1;
        installments.push({
            index: i + 1,
            amount: (isLast ? lastCents : baseCents) / 100,
            dueDate: addRatePlanInterval(startDate, i, interval)
        });
    }

    // C18: Summe + Plausibilitätsprüfung (Rate × Laufzeit ≈ Saldo).
    const sumCents = installments.reduce((acc, r) => acc + Math.round(r.amount * 100), 0);
    const installmentsSum = sumCents / 100;
    const plausibilityOk = sumCents === totalCents;

    return {
        installments,
        rateAmount,
        lastRateAmount,
        lastRateDiffers,
        installmentsSum,
        plausibilityOk
    };
}
